import React, { useState } from 'react';
import {
  Tag,
  Row,
  Col,
  Card,
  Statistic,
  Select,
  DatePicker,
  Alert,
  Button,
} from 'antd';
import {
  ShoppingCartOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useOrders, useOrderStats } from '../../hooks/useOrders';
import { DataTable } from '../../components';
import { formatVND } from '@erpgold/shared';

const { Option } = Select;
const { RangePicker } = DatePicker;

const statusColors: Record<string, string> = {
  pending: 'warning',
  confirmed: 'processing',
  completed: 'success',
  cancelled: 'error',
};

export const OrderList: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    tenantId: undefined as string | undefined,
    status: undefined as string | undefined,
    search: '',
    startDate: undefined as string | undefined,
    endDate: undefined as string | undefined,
  });

  const {
    orders,
    loading,
    error,
    meta,
    setFilters: setTableFilters,
    refetch,
  } = useOrders();

  const { stats, error: statsError } = useOrderStats();

  const handleSearch = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setTableFilters({ ...filters, search: value, page: 1 });
  };

  const handleFilterChange = (key: string, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setTableFilters({ ...newFilters, page: 1 });
  };

  const handleDateChange = (_: any, dateStrings: [string, string]) => {
    const newFilters = {
      ...filters,
      startDate: dateStrings[0] || undefined,
      endDate: dateStrings[1] || undefined,
    };
    setFilters(newFilters);
    setTableFilters({ ...newFilters, page: 1 });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setTableFilters({ ...filters, page, limit: pageSize });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Order ID',
      dataIndex: 'orderId',
      key: 'orderId',
      width: 140,
      render: (text, record) => (
        <a onClick={() => navigate(`/orders/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: 'Tenant',
      dataIndex: 'tenantName',
      key: 'tenantName',
      width: 160,
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      width: 150,
    },
    {
      title: 'Items',
      dataIndex: 'itemCount',
      key: 'itemCount',
      width: 80,
      align: 'center',
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 140,
      align: 'right',
      render: (value) => formatVND(value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={statusColors[status] || 'default'}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Payment',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      width: 120,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm'),
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Select
          placeholder="Filter by tenant"
          allowClear
          showSearch
          style={{ width: '100%' }}
          value={filters.tenantId}
          onChange={(value) => handleFilterChange('tenantId', value)}
          filterOption={(input, option) =>
            (option?.children as unknown as string)
              ?.toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {/* Tenant options loaded dynamically */}
        </Select>
      </Col>
      <Col span={6}>
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: '100%' }}
          value={filters.status}
          onChange={(value) => handleFilterChange('status', value)}
        >
          <Option value="pending">Pending</Option>
          <Option value="confirmed">Confirmed</Option>
          <Option value="completed">Completed</Option>
          <Option value="cancelled">Cancelled</Option>
        </Select>
      </Col>
      <Col span={10}>
        <RangePicker
          style={{ width: '100%' }}
          onChange={handleDateChange}
        />
      </Col>
    </Row>
  );

  if (error || statsError) {
    return (
      <Alert
        message="Lỗi tải dữ liệu"
        description={(error || statsError)?.message || 'Vui lòng thử lại sau'}
        type="error"
        showIcon
        action={<Button onClick={refetch}>Thử lại</Button>}
      />
    );
  }

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Orders Today"
              value={stats?.totalOrdersToday || 0}
              prefix={<ShoppingCartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Revenue Today"
              value={stats?.revenueToday || 0}
              prefix={<DollarOutlined />}
              formatter={(value) => formatVND(value as number)}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Orders"
              value={stats?.pendingOrders || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Avg Order Value"
              value={stats?.avgOrderValue || 0}
              prefix={<RiseOutlined />}
              formatter={(value) => formatVND(value as number)}
            />
          </Card>
        </Col>
      </Row>

      <DataTable
        title="Order Management"
        data={orders}
        columns={columns}
        loading={loading}
        rowKey="id"
        searchable
        searchPlaceholder="Search by order ID or customer..."
        onSearch={handleSearch}
        onRefresh={refetch}
        filters={filterContent}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: handlePageChange,
        }}
        onRowClick={(record) => navigate(`/orders/${record.id}`)}
        actionColumn={{
          width: 80,
          fixed: 'right',
          actions: (record) => [
            {
              key: 'view',
              label: 'View Details',
              icon: <EyeOutlined />,
              onClick: () => navigate(`/orders/${record.id}`),
            },
          ],
        }}
      />
    </div>
  );
};

export default OrderList;
